import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import {
  AcademicCapIcon,
  ArrowRightIcon,
  ArrowTrendingUpIcon,
  BellIcon,
  BookOpenIcon,
  CalendarIcon,
  CheckCircleIcon,
  CheckIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  FireIcon,
  PlayIcon,
  PlusIcon,
  SparklesIcon,
  StarIcon,
  TrophyIcon,
  UserIcon,
  XCircleIcon
} from '@heroicons/react/24/outline'
import { CheckCircleIcon as CheckCircleSolid, StarIcon as StarSolidIcon } from '@heroicons/react/24/solid'
import { EmptyState, NetworkError } from '../../components/ui/ErrorBoundary'
import OnboardingGuide, { studentDashboardSteps, useOnboarding } from '../../components/ui/OnboardingGuide'
import { StudentPageLayout } from '../../components/student/StudentPageLayout'
import { StudentSection } from '../../components/student/StudentSection'

// ============================================================================
// Types & Interfaces
// ============================================================================

interface RecentCourse {
  id: string
  title: string
  description: string
  teacher: {
    user: {
      firstName: string
      lastName: string
    }
  }
  category: string
  price: number
  level: string
  isNew?: boolean
  rating?: number
}

interface DashboardStats {
  totalCourses: number
  completedLessons: number
  upcomingClasses: number
  studyHours: number
}

interface UpcomingClass {
  id: string
  title: string
  teacher: string
  time: string
  duration: string
  type: 'online' | 'offline'
  status: 'confirmed' | 'pending'
}

interface RecentActivity {
  id: string
  type: 'lesson_completed' | 'course_enrolled' | 'assignment_submitted'
  title: string
  description: string
  time: string
  icon: string
}

interface Notification {
  id: string
  type: 'success' | 'warning' | 'info' | 'error'
  title: string
  message: string
  time: string
  isNew: boolean
}

// ============================================================================
// Sub-Components
// ============================================================================

const LearningHeatmap = ({ activities }: { activities?: any[] }) => {
  const weeks = 52
  const days = 7

  const generateData = useMemo(() => {
    const data: number[] = new Array(weeks * days).fill(0)
    
    if (activities && activities.length > 0) {
      // 从真实活动数据计算热力值
      const now = new Date()
      const yearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate())
      
      activities.forEach(activity => {
        const activityDate = new Date(activity.createdAt || activity.date)
        if (activityDate >= yearAgo && activityDate <= now) {
          const diffDays = Math.floor((now.getTime() - activityDate.getTime()) / (1000 * 60 * 60 * 24))
          const index = (weeks * days - 1) - diffDays
          if (index >= 0 && index < data.length) {
            // 根据活动类型增加热力值
            const increment = activity.type === 'course_completed' ? 2 : 1
            data[index] = Math.min((data[index] || 0) + increment, 4)
          }
        }
      })
    }
    
    return data
  }, [activities])

  const data = generateData

  const getColor = (level: number) => {
    switch (level) {
      case 0:
        return 'bg-slate-100'
      case 1:
        return 'bg-emerald-200'
      case 2:
        return 'bg-emerald-300'
      case 3:
        return 'bg-emerald-400'
      case 4:
        return 'bg-emerald-600'
      default:
        return 'bg-slate-100'
    }
  }

  return (
    <div className="w-full overflow-x-auto pb-2">
      <div className="flex gap-1 min-w-max">
        {Array.from({ length: weeks }).map((_, w) => (
          <div key={w} className="flex flex-col gap-1">
            {Array.from({ length: days }).map((_, d) => (
              <div
                key={`${w}-${d}`}
                className={`w-3 h-3 rounded-sm ${getColor(data[w * 7 + d])}`}
                title={`学习热度: ${data[w * 7 + d]}`}
              />
            ))}
          </div>
        ))}
      </div>
      <div className="flex items-center justify-end mt-2 gap-2 text-xs text-slate-500">
        <span>Less</span>
        <div className="w-3 h-3 bg-slate-100 rounded-sm" />
        <div className="w-3 h-3 bg-emerald-200 rounded-sm" />
        <div className="w-3 h-3 bg-emerald-400 rounded-sm" />
        <div className="w-3 h-3 bg-emerald-600 rounded-sm" />
        <span>More</span>
      </div>
    </div>
  )
}

const DailyTasks = () => {
  const [tasks, setTasks] = useState<Array<{ id: string; text: string; completed: boolean }>>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchTasks()
  }, [])

  const fetchTasks = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) return
      
      const response = await fetch('/api/students/tasks', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        // 合并真实任务和 AI 建议
        const allTasks = [
          ...(data.tasks || []).map((task: any) => ({
            id: task.id,
            text: task.title,
            completed: task.status === 'completed'
          })),
          ...(data.aiSuggestions || []).slice(0, 2).map((suggestion: any) => ({
            id: suggestion.id,
            text: suggestion.title,
            completed: false
          }))
        ].slice(0, 5)
        
        setTasks(allTasks.length > 0 ? allTasks : [
          { id: 'default-1', text: '今日暂无学习任务', completed: false }
        ])
      }
    } catch (error) {
      console.error('获取任务失败:', error)
      setTasks([{ id: 'default-1', text: '获取任务失败，请刷新重试', completed: false }])
    } finally {
      setLoading(false)
    }
  }

  const toggleTask = (id: string) => {
    setTasks((prev) =>
      prev.map((task) => (task.id === id ? { ...task, completed: !task.completed } : task))
    )
  }

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center">
          <CheckCircleIcon className="w-5 h-5 text-indigo-500 mr-2" />
          今日学习清单
        </h3>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-12 bg-slate-100 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
      <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center">
        <CheckCircleIcon className="w-5 h-5 text-indigo-500 mr-2" />
        今日学习清单
      </h3>
      <div className="space-y-3">
        {tasks.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">暂无任务，去添加一些吧~</p>
        ) : (
          tasks.map((task) => (
            <button
              key={task.id}
              type="button"
              onClick={() => toggleTask(task.id)}
              className={`w-full flex items-center text-left p-3 rounded-xl border transition-all ${
                task.completed
                  ? 'bg-indigo-50/40 border-indigo-100'
                  : 'bg-white border-slate-200 hover:border-indigo-200 hover:shadow-sm'
              }`}
            >
              <span
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-3 transition-colors ${
                  task.completed ? 'bg-indigo-500 border-indigo-500' : 'border-slate-300'
                }`}
              >
                {task.completed && <CheckIcon className="w-3 h-3 text-white" />}
              </span>
              <span className={`text-sm ${task.completed ? 'text-slate-400 line-through' : 'text-slate-700'}`}>
                {task.text}
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  )
}

const TimelineSchedule = ({ classes }: { classes: UpcomingClass[] }) => {
  if (classes.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 h-full flex flex-col items-center justify-center text-center">
        <div className="w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center mb-4">
          <CalendarIcon className="w-8 h-8 text-indigo-400" />
        </div>
        <h3 className="text-slate-900 font-semibold mb-1">今日暂无课程</h3>
        <p className="text-slate-500 text-sm mb-4">安排一下自习或复盘时间吧</p>
        <Link to="/courses" className="text-indigo-600 text-sm font-semibold hover:underline">
          浏览更多课程
        </Link>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 h-full">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-bold text-slate-900 flex items-center">
          <ClockIcon className="w-5 h-5 text-indigo-500 mr-2" />
          今日课程安排
        </h3>
        <Link to="/student/schedule" className="text-xs text-slate-500 hover:text-indigo-600">
          查看全部 &rarr;
        </Link>
      </div>
      <div className="relative pl-4 border-l-2 border-slate-100 space-y-8">
        {classes.map((cls, index) => (
          <div key={cls.id} className="relative">
            <div
              className={`absolute -left-[21px] top-0 w-3 h-3 rounded-full border-2 ${
                index === 0
                  ? 'bg-indigo-500 border-indigo-100 ring-4 ring-indigo-50'
                  : 'bg-slate-300 border-white'
              }`}
            />
            <div className="bg-slate-50 rounded-xl p-4 border border-transparent hover:border-indigo-100 hover:bg-indigo-50 transition">
              <div className="flex items-start justify-between mb-2">
                <span className="text-sm font-semibold text-slate-900">{cls.time}</span>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full ${
                    cls.type === 'online' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'
                  }`}
                >
                  {cls.type === 'online' ? '线上' : '线下'}
                </span>
              </div>
              <h4 className="text-sm font-medium text-indigo-900 mb-1">{cls.title}</h4>
              <div className="flex items-center text-xs text-slate-500 gap-3">
                <span className="inline-flex items-center gap-1">
                  <UserIcon className="w-3 h-3" />
                  {cls.teacher}
                </span>
                <span className="inline-flex items-center gap-1">
                  <ClockIcon className="w-3 h-3" />
                  {cls.duration}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ============================================================================
// Main Component
// ============================================================================

const StudentDashboard = () => {
  const { user } = useAuthStore()
  const [recentCourses, setRecentCourses] = useState<RecentCourse[]>([])
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [upcomingClasses, setUpcomingClasses] = useState<UpcomingClass[]>([])
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([])
  const [allActivities, setAllActivities] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [error, setError] = useState<string | null>(null)

  const notificationVariants = {
    success: {
      icon: CheckCircleIcon,
      container: 'bg-emerald-50 border border-emerald-100',
      iconWrapper: 'bg-emerald-100 text-emerald-600'
    },
    warning: {
      icon: ExclamationTriangleIcon,
      container: 'bg-amber-50 border border-amber-100',
      iconWrapper: 'bg-amber-100 text-amber-600'
    },
    info: {
      icon: BellIcon,
      container: 'bg-blue-50 border border-blue-100',
      iconWrapper: 'bg-blue-100 text-blue-600'
    },
    error: {
      icon: XCircleIcon,
      container: 'bg-rose-50 border border-rose-100',
      iconWrapper: 'bg-rose-100 text-rose-600'
    }
  } as const

  const { isOpen, complete, skip } = useOnboarding('student-dashboard')

  const activeDays = 156
  const currentStreak = 12
  const longestStreak = 48
  const unreadNotifications = notifications.filter((notification) => notification.isNew).length

  const layoutMetrics = useMemo(
    () => [
      {
        label: '课程进度',
        value: loading ? '加载中…' : stats ? `${stats.completedLessons} 课时` : '--',
        helper: stats ? `共 ${stats.totalCourses} 门课程` : '等待同步'
      },
      {
        label: '即将上课',
        value: loading ? '…' : `${upcomingClasses.length} 节`,
        helper: '未来 24 小时'
      },
      {
        label: '活跃打卡',
        value: `${activeDays} 天`,
        helper: `连续 ${currentStreak} 天`
      },
      {
        label: '未读提醒',
        value: `${unreadNotifications} 条`,
        helper: '实时同步'
      }
    ],
    [stats, loading, upcomingClasses.length, unreadNotifications, activeDays, currentStreak]
  )

  const nextClass = upcomingClasses[0]

  const heroExtra = (
    <div className="space-y-4 text-white">
      <p className="text-sm text-white/80">
        今日累计学习 {stats?.studyHours ?? '--'} 小时 · 连续打卡 {currentStreak} 天
      </p>
      <div className="bg-white/10 rounded-2xl border border-white/20 p-4">
        <p className="text-xs uppercase tracking-[0.3em] text-white/70 mb-2">下一节课</p>
        {nextClass ? (
          <>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-lg font-semibold">{nextClass.title}</p>
                <p className="text-sm text-white/70 mt-1">导师：{nextClass.teacher}</p>
              </div>
              <span className="px-3 py-1 rounded-full text-xs bg-white/15">
                {nextClass.type === 'online' ? '线上' : '线下'}
              </span>
            </div>
            <div className="mt-4 flex items-center text-sm text-white/70 gap-4">
              <span className="inline-flex items-center gap-1">
                <ClockIcon className="w-4 h-4" />
                {nextClass.time}
              </span>
              <span className="inline-flex items-center gap-1">
                <CalendarIcon className="w-4 h-4" />
                {nextClass.duration}
              </span>
            </div>
          </>
        ) : (
          <div className="text-sm text-white/80">今天暂无课程安排，利用时间巩固薄弱知识点吧。</div>
        )}
      </div>
    </div>
  )

  const heroActions = (
    <>
      <Link
        to="/student/ai"
        className="inline-flex items-center px-4 py-2.5 rounded-2xl bg-white text-indigo-600 font-semibold text-sm shadow-md hover:bg-slate-100 transition-colors"
      >
        <SparklesIcon className="w-4 h-4 mr-2" />
        AI 学习规划
      </Link>
      <Link
        to="/student/bookings"
        className="inline-flex items-center px-4 py-2.5 rounded-2xl border border-white/40 text-white font-semibold text-sm hover:bg-white/10 transition-colors"
      >
        <CalendarIcon className="w-4 h-4 mr-2" />
        查看课程表
      </Link>
    </>
  )

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      setError(null)

      const token = localStorage.getItem('token')
      if (!token) {
        setError('请先登录')
        return
      }

      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }

      // 并行请求所有数据
      const [statsRes, upcomingRes, activitiesRes, notificationsRes, coursesRes] = await Promise.allSettled([
        fetch('/api/students/stats', { headers }),
        fetch('/api/students/upcoming-classes?limit=5', { headers }),
        fetch('/api/students/activities', { headers }),
        fetch('/api/students/notifications/summary', { headers }),
        fetch('/api/courses/me/learning', { headers })
      ])

      // 处理统计数据
      if (statsRes.status === 'fulfilled' && statsRes.value.ok) {
        const statsData = await statsRes.value.json()
        setStats({
          totalCourses: statsData.totalBookings || 0,
          completedLessons: statsData.completedBookings || 0,
          upcomingClasses: statsData.activeDaysLast7 || 0,
          studyHours: Math.round(statsData.totalHours || 0)
        })
      } else {
        setStats({ totalCourses: 0, completedLessons: 0, upcomingClasses: 0, studyHours: 0 })
      }

      // 处理即将到来的课程
      if (upcomingRes.status === 'fulfilled' && upcomingRes.value.ok) {
        const upcomingData = await upcomingRes.value.json()
        setUpcomingClasses(upcomingData.map((cls: any) => ({
          id: cls.id,
          title: cls.title || '课程',
          teacher: cls.teacher || '老师',
          time: cls.time || '',
          duration: cls.duration || '1小时',
          type: cls.type || 'online',
          status: cls.status || 'confirmed'
        })))
      } else {
        setUpcomingClasses([])
      }

      // 处理活动记录
      if (activitiesRes.status === 'fulfilled' && activitiesRes.value.ok) {
        const activitiesData = await activitiesRes.value.json()
        // 保存所有活动用于热力图
        setAllActivities(activitiesData || [])
        setRecentActivities(activitiesData.slice(0, 10).map((activity: any) => {
          const typeIcons: Record<string, string> = {
            class: '📚',
            exam: '📝',
            task: '✅'
          }
          const timeDiff = Date.now() - new Date(activity.timestamp).getTime()
          const hours = Math.floor(timeDiff / (1000 * 60 * 60))
          const days = Math.floor(hours / 24)
          const timeStr = days > 0 ? `${days} 天前` : hours > 0 ? `${hours} 小时前` : '刚刚'
          
          return {
            id: activity.id,
            type: activity.type === 'class' ? 'lesson_completed' : activity.type === 'exam' ? 'assignment_submitted' : 'course_enrolled',
            title: activity.title,
            description: activity.description || '',
            time: timeStr,
            icon: typeIcons[activity.type] || '📌'
          }
        }))
      } else {
        setRecentActivities([])
      }

      // 处理通知
      if (notificationsRes.status === 'fulfilled' && notificationsRes.value.ok) {
        const notificationsData = await notificationsRes.value.json()
        setNotifications((notificationsData.latest || []).map((n: any) => {
          const timeDiff = Date.now() - new Date(n.createdAt).getTime()
          const hours = Math.floor(timeDiff / (1000 * 60 * 60))
          const days = Math.floor(hours / 24)
          const minutes = Math.floor(timeDiff / (1000 * 60))
          const timeStr = days > 0 ? `${days} 天前` : hours > 0 ? `${hours} 小时前` : minutes > 0 ? `${minutes} 分钟前` : '刚刚'
          
          return {
            id: n.id,
            type: (n.type === 'success' || n.type === 'warning' || n.type === 'info' || n.type === 'error') ? n.type : 'info',
            title: n.title,
            message: n.message,
            time: timeStr,
            isNew: !n.read
          }
        }))
      } else {
        setNotifications([])
      }

      // 处理学习中的课程
      if (coursesRes.status === 'fulfilled' && coursesRes.value.ok) {
        const coursesData = await coursesRes.value.json()
        // 确保 coursesArray 是一个数组
        let coursesArray: any[] = []
        if (Array.isArray(coursesData)) {
          coursesArray = coursesData
        } else if (coursesData && Array.isArray(coursesData.data)) {
          coursesArray = coursesData.data
        } else if (coursesData && Array.isArray(coursesData.courses)) {
          coursesArray = coursesData.courses
        } else if (coursesData && typeof coursesData === 'object') {
          // 尝试从对象中提取数组
          const possibleArrays = Object.values(coursesData).filter(Array.isArray)
          if (possibleArrays.length > 0) {
            coursesArray = possibleArrays[0] as any[]
          }
        }
        
        setRecentCourses(coursesArray.slice(0, 5).map((course: any) => ({
          id: course.id,
          title: course.title || course.subject || '课程',
          description: course.description || '',
          teacher: {
            user: {
              firstName: course.teacher?.user?.firstName || course.teacher?.firstName || '',
              lastName: course.teacher?.user?.lastName || course.teacher?.lastName || '老师'
            }
          },
          category: course.subject || course.category || '学习',
          price: course.finalPricePerHour || course.price || 0,
          level: course.level || '初级',
          isNew: false,
          rating: course.rating || 4.5
        })))
      } else {
        setRecentCourses([])
      }

    } catch (err) {
      console.error('获取仪表板数据失败:', err)
      setError('获取数据失败，请稍后重试')
    } finally {
      setLoading(false)
    }
  }

  const applicationStatus = {
    currentStage: 2,
    stages: [
      { name: '选校定位', date: '2023-12-01', status: 'completed' },
      { name: '文书准备', date: '2024-01-15', status: 'completed' },
      { name: '申请递交', date: '2024-02-01', status: 'in_progress' },
      { name: '等待结果', date: '预计 2024-04', status: 'pending' },
      { name: '录取 Offer', date: '预计 2024-05', status: 'pending' }
    ]
  }

  if (error) {
    return (
      <StudentPageLayout title="学习控制台" description="同步学习数据失败，请稍后重试。" metrics={[]}>
        <div className="bg-white rounded-3xl border border-slate-100 p-8">
          <NetworkError onRetry={fetchDashboardData} />
        </div>
      </StudentPageLayout>
    )
  }

  return (
    <>
      <OnboardingGuide steps={studentDashboardSteps} isOpen={isOpen} onComplete={complete} onSkip={skip} />
      <StudentPageLayout
        title={`欢迎回来，${user?.firstName || '学习者'}！`}
        description="实时洞察学习轨迹、即将上课与留学进度，AI 会陪你一起冲刺目标院校。"
        badge="STUDENT DASHBOARD"
        metrics={layoutMetrics}
        heroExtra={heroExtra}
        actions={heroActions}
        contentWidth="wide"
      >
        {/* Section 1: 成长旅程总览 */}
        <StudentSection
          title="成长旅程总览"
          description="掌控留学申请、任务清单与关键提醒，让每日的努力都可视化。"
          icon={TrophyIcon}
        >
          <div className="grid lg:grid-cols-3 gap-4 sm:gap-6">
            {/* 留学申请进度 */}
            <div className="lg:col-span-2 bg-white rounded-2xl sm:rounded-3xl shadow-sm border border-slate-100 p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h2 className="text-base sm:text-lg font-bold text-slate-900 flex items-center">
                  <SparklesIcon className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500 mr-1.5 sm:mr-2" />
                  留学申请进度
                </h2>
                <Link
                  to="/student/application"
                  className="text-xs sm:text-sm text-indigo-600 font-semibold hover:text-indigo-700"
                >
                  查看详情 &rarr;
                </Link>
              </div>
              {/* 进度条 - 移动端横向滚动 */}
              <div className="relative overflow-x-auto pb-4 -mx-4 px-4 sm:mx-0 sm:px-0 sm:overflow-visible">
                <div className="relative min-w-[500px] sm:min-w-0 px-2 sm:px-4 pb-2 sm:pb-6">
                  <div className="absolute top-5 left-6 right-6 sm:left-4 sm:right-4 h-1 bg-slate-100 rounded-full" />
                  <div
                    className="absolute top-5 left-6 sm:left-4 h-1 bg-indigo-500 rounded-full transition-all duration-700"
                    style={{
                      width: `calc(${(applicationStatus.currentStage / (applicationStatus.stages.length - 1)) * 100}% - 16px)`
                    }}
                  />
                  <div className="relative flex justify-between">
                    {applicationStatus.stages.map((stage, index) => {
                      const isCompleted = index <= applicationStatus.currentStage
                      const isCurrent = index === applicationStatus.currentStage
                      return (
                        <div key={stage.name} className="flex flex-col items-center w-16 sm:w-20 text-center">
                          <div
                            className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center border-3 sm:border-4 bg-white transition-all text-xs sm:text-sm ${
                              isCompleted ? 'border-indigo-500 text-indigo-600' : 'border-slate-200 text-slate-300'
                            } ${isCurrent ? 'ring-2 sm:ring-4 ring-indigo-100 scale-105' : ''}`}
                          >
                            {isCompleted ? <CheckCircleSolid className="h-4 w-4 sm:h-5 sm:w-5" /> : index + 1}
                          </div>
                          <p
                            className={`mt-2 sm:mt-3 text-[10px] sm:text-xs font-semibold ${
                              isCompleted ? 'text-slate-900' : 'text-slate-400'
                            }`}
                          >
                            {stage.name}
                          </p>
                          <p className="text-[8px] sm:text-[10px] text-slate-400 mt-0.5">{stage.date}</p>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
              <div className="mt-4 sm:mt-6 bg-indigo-50/70 rounded-xl sm:rounded-2xl border border-indigo-100 p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0 sm:justify-between">
                <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-indigo-800">
                  <span className="flex h-2.5 w-2.5 sm:h-3 sm:w-3 relative flex-shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 sm:h-3 sm:w-3 bg-indigo-500" />
                  </span>
                  <span className="line-clamp-2">当前状态：正在准备 {applicationStatus.stages[applicationStatus.currentStage].name} 材料，请留意截止时间。</span>
                </div>
                <button className="text-xs bg-white text-indigo-600 px-3 py-1.5 rounded-lg border border-indigo-100 font-medium hover:bg-indigo-50 transition self-start sm:self-auto flex-shrink-0">
                  更新进度
                </button>
              </div>
            </div>
            <DailyTasks />
          </div>
        </StudentSection>

        {/* Section 2: 学习节奏与课表 */}
        <StudentSection
          title="学习节奏与课表"
          description="AI 可视化你的课程节奏与打卡轨迹，帮你及时调整学习状态。"
          icon={ClockIcon}
        >
          <div className="grid lg:grid-cols-3 gap-6">
            <TimelineSchedule classes={upcomingClasses} />
            <div className="lg:col-span-2 bg-white rounded-3xl shadow-sm border border-slate-100 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-slate-900 flex items-center">
                  <FireIcon className="w-5 h-5 text-orange-500 mr-2" />
                  学习投入度
                </h3>
                <span className="text-xs text-slate-500">过去一年</span>
              </div>
              <LearningHeatmap activities={allActivities} />
              <div className="mt-6 grid grid-cols-3 gap-4 border-t border-slate-100 pt-6 text-center">
                <div>
                  <div className="text-2xl font-bold text-slate-900">{activeDays}</div>
                  <div className="text-xs text-slate-500">总活跃天数</div>
                </div>
                <div className="border-x border-slate-100">
                  <div className="text-2xl font-bold text-slate-900">{currentStreak}</div>
                  <div className="text-xs text-slate-500">当前连续打卡</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900">{longestStreak}</div>
                  <div className="text-xs text-slate-500">最长连续打卡</div>
                </div>
              </div>
            </div>
          </div>
        </StudentSection>

        {/* Section 3: 最近学习轨迹 */}
        <StudentSection
          title="最近学习轨迹"
          description="基于近 14 天的学习记录实时更新，结合 AI 推荐下一步课程。"
          icon={ArrowTrendingUpIcon}
        >
          {recentCourses.length === 0 ? (
            <EmptyState
              title="暂无课程记录"
              description="去发现新的课程，开启学习旅程。"
              actionText="前往课程广场"
              onAction={() => window.location.assign('/courses')}
            />
          ) : (
            <div className="space-y-4">
              {recentCourses.map((course, index) => (
                <div
                  key={course.id}
                  className="p-4 rounded-2xl border border-slate-100 hover:border-indigo-200 hover:shadow-md transition bg-white"
                >
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div>
                      <p className="text-sm uppercase tracking-wide text-slate-400">{course.category}</p>
                      <h4 className="text-xl font-bold text-slate-900 mt-1">{course.title}</h4>
                      <p className="text-sm text-slate-500 mt-1">{course.description}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {index === 0 && (
                        <span className="inline-flex items-center text-xs font-semibold text-orange-600 bg-orange-50 px-2 py-1 rounded-full">
                          <ArrowTrendingUpIcon className="w-4 h-4 mr-1" />
                          热门
                        </span>
                      )}
                      {course.isNew && (
                        <span className="inline-flex items-center text-xs font-semibold text-indigo-600 bg-indigo-50 px-2 py-1 rounded-full">
                          NEW
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-slate-500">
                    <span className="inline-flex items-center">
                      <UserIcon className="w-4 h-4 mr-1 text-slate-400" />
                      {course.teacher.user.firstName} {course.teacher.user.lastName}
                    </span>
                    <span className="inline-flex items-center">
                      <AcademicCapIcon className="w-4 h-4 mr-1 text-slate-400" />
                      {course.level}
                    </span>
                    <span className="inline-flex items-center font-semibold text-slate-900">
                      <StarSolidIcon className="w-4 h-4 mr-1 text-yellow-400" />
                      {(course.rating ?? 4.8).toFixed(1)}
                    </span>
                    <span className="text-slate-900 font-bold">¥{course.price}</span>
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-slate-400">最近更新 · 自动同步学习计划</span>
                    <Link
                      to={`/courses/${course.id}`}
                      className="inline-flex items-center text-sm font-semibold text-indigo-600 hover:text-indigo-700"
                    >
                      <PlayIcon className="w-4 h-4 mr-1" />
                      继续学习
                      <ArrowRightIcon className="w-4 h-4 ml-1" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </StudentSection>

        {/* Section 4: 提醒中心与学习动态 */}
        <StudentSection
          title="提醒中心与学习动态"
          description="所有提醒、任务与动态在此统一呈现，及时响应每一个关键信号。"
          icon={BellIcon}
        >
          <div className="grid lg:grid-cols-2 gap-6">
            {/* 学习提醒 */}
            <div className="bg-slate-50 rounded-3xl border border-slate-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <BellIcon className="w-5 h-5 text-blue-500" />
                  学习提醒
                </h3>
                <span className="text-xs text-slate-500">实时同步</span>
              </div>
              <div className="space-y-4 max-h-80 overflow-y-auto pr-1 scrollbar-hide">
                {notifications.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-6">暂时没有新的提醒</p>
                ) : (
                  notifications.map((notification) => {
                    const variant = notificationVariants[notification.type]
                    const Icon = variant.icon
                    return (
                      <div
                        key={notification.id}
                        className={`p-4 rounded-2xl flex items-start gap-3 ${variant.container}`}
                      >
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center ${variant.iconWrapper}`}
                        >
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-semibold text-slate-900">{notification.title}</p>
                            {notification.isNew && (
                              <span className="text-[10px] font-semibold text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                                NEW
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 mt-1">{notification.message}</p>
                          <p className="text-xs text-slate-400 mt-2">{notification.time}</p>
                        </div>
                      </div>
                    )
                  })
                )}
              </div>
            </div>

            {/* 学习动态 */}
            <div className="bg-slate-50 rounded-3xl border border-slate-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <SparklesIcon className="w-5 h-5 text-purple-500" />
                  学习动态
                </h3>
                <Link to="/student/profile" className="text-xs font-medium text-slate-500 hover:text-indigo-600">
                  查看学习档案
                </Link>
              </div>
              <div className="space-y-4 max-h-80 overflow-y-auto pr-1 scrollbar-hide">
                {recentActivities.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-6">暂未产生学习记录</p>
                ) : (
                  recentActivities.map((activity, index) => (
                    <div
                      key={activity.id}
                      className="relative pl-10 pb-4 border-b border-slate-200 last:border-0 last:pb-0"
                    >
                      <div className="absolute left-0 top-2 flex flex-col items-center">
                        <span className="w-7 h-7 rounded-full bg-indigo-50 text-base flex items-center justify-center">
                          {activity.icon}
                        </span>
                        {index !== recentActivities.length - 1 && (
                          <span className="flex-1 w-px bg-slate-100 mt-1" />
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-slate-900">{activity.title}</p>
                        <span className="text-xs text-slate-400">{activity.time}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{activity.description}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </StudentSection>

        {/* Section 5: 智能助手与快捷入口 */}
        <StudentSection
          title="智能助手与快捷入口"
          description="AI 工具与常用入口一站式呈现，让操作更高效。"
          icon={SparklesIcon}
        >
          <div className="grid lg:grid-cols-3 gap-4 sm:gap-6">
            {/* AI 智能选校助手 */}
            <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-2xl sm:rounded-3xl text-white p-4 sm:p-6 relative overflow-hidden shadow-lg">
              <div className="absolute top-0 right-0 w-24 h-24 sm:w-32 sm:h-32 bg-white/10 rounded-full blur-2xl -mr-8 -mt-8 sm:-mr-10 sm:-mt-10" />
              <div className="relative z-10 space-y-3 sm:space-y-4">
                <div className="flex items-center gap-2">
                  <SparklesIcon className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-300 animate-pulse" />
                  <h2 className="text-base sm:text-lg font-bold">AI 智能选校助手</h2>
                </div>
                <p className="text-indigo-100 text-xs sm:text-sm leading-relaxed">
                  基于 GPA、语言成绩与兴趣偏好，AI 为你匹配全球院校，随时输出备选清单。
                </p>
                <div className="bg-white/10 rounded-lg p-2.5 sm:p-3 backdrop-blur-sm border border-white/10">
                  <div className="text-[10px] sm:text-xs text-indigo-200 mb-1">今日推荐院校</div>
                  <div className="flex items-center justify-between font-semibold text-sm">
                    <span className="truncate mr-2">斯坦福大学</span>
                    <span className="text-green-300 text-[10px] sm:text-xs bg-green-500/20 px-2 py-0.5 rounded flex-shrink-0">
                      匹配度 92%
                    </span>
                  </div>
                </div>
                <Link
                  to="/student/ai?tab=school-selection"
                  className="block w-full bg-white text-indigo-600 text-center py-2.5 rounded-lg font-semibold hover:bg-indigo-50 transition-colors shadow-md text-sm"
                >
                  开始智能选校
                </Link>
              </div>
            </div>

            {/* 快捷入口 */}
            <div className="lg:col-span-2 space-y-4 sm:space-y-6">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
                <Link
                  to="/student/ai"
                  className="flex flex-col items-center p-3 sm:p-4 border border-slate-100 rounded-xl hover:border-purple-300 hover:bg-purple-50 transition-all group"
                >
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-purple-100 rounded-full flex items-center justify-center mb-1.5 sm:mb-2 group-hover:bg-purple-200 text-purple-600">
                    <StarIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                  </div>
                  <span className="text-xs sm:text-sm font-medium text-slate-700 text-center">AI 学习助手</span>
                </Link>
                <Link
                  to="/courses"
                  className="flex flex-col items-center p-3 sm:p-4 border border-slate-100 rounded-xl hover:border-blue-300 hover:bg-blue-50 transition-all group"
                >
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center mb-1.5 sm:mb-2 group-hover:bg-blue-200 text-blue-600">
                    <BookOpenIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                  </div>
                  <span className="text-xs sm:text-sm font-medium text-slate-700 text-center">发现课程</span>
                </Link>
                <Link
                  to="/student/bookings"
                  className="flex flex-col items-center p-3 sm:p-4 border border-slate-100 rounded-xl hover:border-green-300 hover:bg-green-50 transition-all group"
                >
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-green-100 rounded-full flex items-center justify-center mb-1.5 sm:mb-2 group-hover:bg-green-200 text-green-600">
                    <CalendarIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                  </div>
                  <span className="text-xs sm:text-sm font-medium text-slate-700 text-center">我的预约</span>
                </Link>
                <Link
                  to="/student/profile"
                  className="flex flex-col items-center p-3 sm:p-4 border border-slate-100 rounded-xl hover:border-slate-300 hover:bg-slate-50 transition-all group"
                >
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-slate-100 rounded-full flex items-center justify-center mb-1.5 sm:mb-2 group-hover:bg-slate-200 text-slate-600">
                    <UserIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                  </div>
                  <span className="text-xs sm:text-sm font-medium text-slate-700 text-center">个人设置</span>
                </Link>
              </div>

              {/* AI 提问入口 */}
              <div className="p-3 sm:p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl sm:rounded-2xl border border-blue-100 flex items-center justify-between gap-3">
                <div className="flex items-center min-w-0">
                  <span className="text-xl sm:text-2xl mr-2 sm:mr-3 flex-shrink-0">🤖</span>
                  <div className="min-w-0">
                    <h4 className="text-xs sm:text-sm font-bold text-slate-900">遇到学习难题？</h4>
                    <p className="text-[10px] sm:text-xs text-slate-600 truncate">上传题目图片，AI 老师为你深度解析</p>
                  </div>
                </div>
                <Link
                  to="/student/ai"
                  className="text-[10px] sm:text-xs bg-white text-indigo-600 px-2.5 sm:px-3 py-1.5 rounded-md font-medium shadow-sm hover:shadow transition-all flex-shrink-0"
                >
                  立即提问
                </Link>
              </div>
            </div>
          </div>
        </StudentSection>
      </StudentPageLayout>
    </>
  )
}

export default StudentDashboard